import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Backdrop, Box, CircularProgress, Modal, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { Suspense, useEffect, useLayoutEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { syncClarityRoute } from './analytics/clarity';
import PageHelmet from './components/SEO/PageHelmet';
import { isAdminPath, needsStandaloneNoIndex } from './config/privatePageRobots';
import { trackGtmPageView } from './analytics/gtm';
import { trackMetaPageView } from './analytics/metaPixel';
import yovoAssistIcon from './assets/brand/yovo-assist-icon.png';
import ErrorBoundary from './components/ErrorBoundary';
import Homepage from './components/Homepage/Homepage';
import { isLcpShellPath, removeLcpShell } from './components/Header/lcpShell';
import RouteSuspenseFallback from './components/Loader/RouteSuspenseFallback';
import { warmVerticalLoaderImages } from './components/Loader/utils/warmLoaderAssets';
import { RequireFlightSearchRoute } from './components/common/RequireFlightSearch';
import { RequireSearchParamRoute } from './components/common/RequireSearchParam';
import { POLICY_ROUTES } from './config/companyConfig';
import {
  FLIGHT_SESSION_QUERY_PARAM,
  HOTEL_SESSION_QUERY_PARAM,
} from './constants/sessionRoute.constants';
import {
    isHomepageRoute,
    isPrimaryMobileNavRoute,
    MOBILE_NAV_PORTAL_SELECTOR,
    normalizePathname,
} from './config/mobileNavConfig';
import { isAppSharePath, toWebsitePathname } from './components/app-links/appStoreFallback';
import { ThemeProvider } from './theme';
import './components/flight_select/styles/FlightDetailsAndFarePlans.css';
import slugify from './utils/slugify';

// import Header from './components/Header';
// import NavigationHeader from './components/NavigationHeader';
import { AuthModalProvider, AuthProvider, useAuth } from './features/auth';
import { SearchProvider } from './context/SearchContext';
// import FlightBookingPage from './components/FlightBookingPage';
// import FlightListings from './components/FlightListings/FlightListings';

// Lazy load route screens. The homepage stays eager so it paints without a second chunk round trip.
const AccountPage = React.lazy(() => import('./components/Auth/AccountPage'));
const Chatbot = React.lazy(() => import('./components/Chatbot/Chatbot'));
const NotFoundPage = React.lazy(() => import('./components/common/NotFoundPage'));
const AppStoreFallbackDrawer = React.lazy(() => import('./components/app-links/AppStoreFallbackDrawer'));
// const AirportSearchDemo = React.lazy(() => import('./pages/AirportSearchDemo'));
// const Homepage2 = React.lazy(() => import('./components/Homepage/YovoTripHomepage'));
const FlightSelect = React.lazy(() => import('./components/flight_select/FlightSelect'));
const DeeplinkFlightSelect = React.lazy(() => import('./components/app-links/DeeplinkFlightSelect'));

const HotelSearchPage = React.lazy(() => import('./components/Hotels/pages/HotelSearchPage'));
const HotelDetailPage = React.lazy(() => import('./components/Hotels/pages/HotelDetailPage'));
const BookingPage = React.lazy(() => import('./components/Hotels/pages/BookingPage'))
const BookingReviewPage = React.lazy(() => import('./components/Hotels/pages/BookingReviewPage'));
const HotelBookingConfirmationPage = React.lazy(() => import('./components/Hotels/pages/HotelBookingConfirmationPage'));
const HotelCancellationReviewPage = React.lazy(() => import('./components/Hotels/pages/HotelCancellationReviewPage'));
const FlightFareCard = React.lazy(() => import('./components/flight_select/FlightFareCard'));
const FlightDetails = React.lazy(() => import('./components/flight_details/FlightDetails'));
const FlightBookingPageNew = React.lazy(() => import('./components/flight_details/FlightBookingPage'));
const SeatMapDemo = React.lazy(() => import('./components/flight_details/SeatMapDemo'));
const MyTrips = React.lazy(() => import('./components/flight_details/MyTrips'));
const SeatSelectionDemo = React.lazy(() => import('./components/flight_details/SeatSelectionDemo'));
const TrainSelect = React.lazy(() => import('./components/train_select/TrainSelect'));
const TrainResultsPage = React.lazy(() => import('./components/train_select/TrainResultsPage'));
const TrainDetailsPage = React.lazy(() => import('./components/train_select/TrainDetailsPage'));
const TrainSchedulePage = React.lazy(() => import('./components/train_select/TrainSchedulePage'));
const PNRStatusPage = React.lazy(() => import('./components/train_select/PNRStatusPage'));
const FlightSearchPage = React.lazy(() => import('./components/FlightSearch/FlightSearchPage'));
const FlightBookingPage = React.lazy(() => import('./components/FlightResults/FlightResults'));
// const FlightResultsDemo = React.lazy(() => import('./pages/FlightResultsDemo'));
const FlightSearchResults = React.lazy(() => import('./components/flight_select/FlightSearchResults'));
const FlightSearchResultsDemo = React.lazy(() => import('./pages/FlightSearchResultsDemo'));
const FlightCard = React.lazy(() => import('./components/flight_select/FlightDetailsPage'));
const FlightCardComponent = React.lazy(() => import('./components/flight_details/FlightCard'));
const CancellationPolicy = React.lazy(() => import('./components/flight_details/CancellationPolicy'));
const TravellerDetails = React.lazy(() => import('./components/flight_details/TravellerDetails'));
const Flight_SSR_Choose = React.lazy(() => import('./components/Flight_SSR/Flight_SSR_Choose'));
const Flight_Preview = React.lazy(() => import('./components/Flight_SSR/Flight_Preview'));
const PaymentPage = React.lazy(() => import('./components/Flight_Payment/PaymentPage'));
const BookingConfirmation = React.lazy(() => import('./components/Flight_Payment/BookingConfirmation'));
const ShortLinkRedirect = React.lazy(() => import('./components/common/ShortLinkRedirect'));
const ViewBookingDetails = React.lazy(() => import('./components/Flight_Payment/ViewBookingDetails'));
const TicketView = React.lazy(() => import('./components/Flight_Payment/TicketView'));
const FlightCancellation = React.lazy(() => import('./components/Flight_Payment/FlightCancellation'));
const CancellationReview = React.lazy(() => import('./components/Flight_Payment/CancellationReview'));
const CancelledBookingDetails = React.lazy(() => import('./components/Flight_Payment/CancelledBookingDetails'));
const ETicket = React.lazy(() => import('./components/Flight_Payment/E_Ticket'));
const SeatSelectionPage = React.lazy(() => import('./components/Flight_SSR/SeatSelectionPage'));
const TestAnimation = React.lazy(() => import('./components/TestAnimation'));
const IstanbulGuide = React.lazy(() => import('./components/Pages/IstanbulGuide'));
const DestinationsPage = React.lazy(() => import('./components/Pages/DestinationsPage'));
const FestivalGuideRoute = React.lazy(() => import('./components/festivals/FestivalGuideRoute'));
const HeritagePage = React.lazy(() => import('./components/Pages/Heritage_Place'));
const PrivacyPolicy = React.lazy(() => import('./components/Pages/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./components/Pages/TermsOfService'));
const CancellationRefundPolicy = React.lazy(() => import('./components/Pages/CancellationRefundPolicy'));
const Contact = React.lazy(() => import('./components/Pages/Contact'));
const FAQPage = React.lazy(() => import('./components/Pages/FAQPage'));
const SitemapPage = React.lazy(() => import('./components/Pages/SitemapPage'));

const FlightSeoSelect = React.lazy(() => import('./components/flight-seo/FlightSeoSelect'));
const HotelDestinationLandingPage = React.lazy(
  () => import('./components/hotel-seo/HotelDestinationLandingPage'),
);

function HeritageLegacyRedirect() {
  const { placeName } = useParams();
  const decoded = decodeURIComponent(placeName || '');
  const slug = slugify(decoded) || decoded;
  return <Navigate to={`/destinations/heritage/${slug}`} replace />;
}

function GoToDeeplinksRedirect() {
  const location = useLocation();
  const rest = location.pathname.replace(/^\/go/, '') || '';
  return <Navigate to={`/deeplinks${rest}${location.search}${location.hash}`} replace />;
}
const TravelGallery = React.lazy(() => import('./components/TravelGallery/TravelGallery'));
const FlightFinancials = React.lazy(() => import('./components/Admin/FlightFinancials'));
const HotelFinancials = React.lazy(() => import('./components/Admin/HotelFinancials'));
const HotelPricingGovernance = React.lazy(() => import('./components/Admin/HotelPricingGovernance'));
const HotelPricingVersionCompare = React.lazy(() => import('./components/Admin/HotelPricingVersionCompare'));
const HotelPricingSimulator = React.lazy(() => import('./components/Admin/HotelPricingSimulator'));
const HotelBookingsConsole = React.lazy(() => import('./components/Admin/HotelBookingsConsole'));
const HotelRecon = React.lazy(() => import('./components/Admin/HotelRecon'));
const HotelOffers = React.lazy(() => import('./components/Admin/HotelOffers'));
const FlightBookingsConsole = React.lazy(() => import('./components/Admin/FlightBookingsConsole'));
const FlightSessionExport = React.lazy(() => import('./components/Admin/FlightSessionExport'));
const InsurancePolicyReport = React.lazy(() => import('./components/Admin/InsurancePolicyReport'));
const CfarReport = React.lazy(() => import('./components/Admin/CfarReport'));
const FlightPricingGovernance = React.lazy(() => import('./components/Admin/FlightPricingGovernance'));
const FlightRecon = React.lazy(() => import('./components/Admin/FlightRecon'));
const FlightOffers = React.lazy(() => import('./components/Admin/FlightOffers'));
const ServiceFeeManagement = React.lazy(() => import('./components/Admin/ServiceFeeManagement'));
const IssueMonitor = React.lazy(() => import('./components/Admin/IssueMonitor'));
const RefundManagement = React.lazy(() => import('./components/Admin/RefundManagement'));
const ServiceRequestManagement = React.lazy(() => import('./components/Admin/ServiceRequestManagement'));
const AdminLayout = React.lazy(() => import('./components/Admin/AdminLayout'));
const AdminPlaceholder = React.lazy(() => import('./components/Admin/AdminPlaceholder'));
const Campaigns = React.lazy(() => import('./components/Admin/Campaigns'));
const CampaignOffers = React.lazy(() => import('./components/Admin/CampaignOffers'));
const BroadcastNotifications = React.lazy(() => import('./components/Admin/BroadcastNotifications'));

const YovoHeader = React.lazy(() => import('./components/Homepage/YovoTripHeader'));
const FlightSearchHeader = React.lazy(() => import('./components/flight_select/FlightSearchHeader'));
const FlightDetailsAndFarePlans = React.lazy(() => import('./components/flight_select/FlightDetailsAndFarePlans'));

// Issue Monitor Page wrapper with auth protection
function IssueMonitorPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();

  React.useEffect(() => {
    if (!authLoading && (!isAuthenticated || !user?.isAdmin)) {
      navigate('/', { replace: true });
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#F5F6FA' }}>
        <CircularProgress sx={{ color: '#181B28' }} />
      </Box>
    );
  }

  if (!isAuthenticated || !user?.isAdmin) {
    return null;
  }

  return (
    <Box sx={{ bgcolor: '#F5F6FA', minHeight: '100vh' }}>
      <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>}>
        {React.createElement(
          React.lazy(() => import('./components/Header/YovoTripHeader')),
          { alwaysBrandBar: true }
        )}
      </React.Suspense>
      <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, md: 3 }, pt: { xs: 2, md: 3 }, pb: 4 }}>
        <IssueMonitor />
      </Box>
    </Box>
  );
}

// Refund Management Page wrapper with auth protection
function RefundManagementPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();

  React.useEffect(() => {
    if (!authLoading && (!isAuthenticated || !user?.isAdmin)) {
      navigate('/', { replace: true });
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#F5F6FA' }}>
        <CircularProgress sx={{ color: '#181B28' }} />
      </Box>
    );
  }

  if (!isAuthenticated || !user?.isAdmin) {
    return null;
  }

  return (
    <Box sx={{ bgcolor: '#F5F6FA', minHeight: '100vh' }}>
      <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>}>
        {React.createElement(
          React.lazy(() => import('./components/Header/YovoTripHeader')),
          { alwaysBrandBar: true }
        )}
      </React.Suspense>
      <RefundManagement />
    </Box>
  );
}

// P0-7B: Service Request Management Page wrapper with auth protection
function ServiceRequestManagementPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();

  React.useEffect(() => {
    if (!authLoading && (!isAuthenticated || !user?.isAdmin)) {
      navigate('/', { replace: true });
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#F5F6FA' }}>
        <CircularProgress sx={{ color: '#181B28' }} />
      </Box>
    );
  }

  if (!isAuthenticated || !user?.isAdmin) {
    return null;
  }

  return (
    <Box sx={{ bgcolor: '#F5F6FA', minHeight: '100vh' }}>
      <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>}>
        {React.createElement(
          React.lazy(() => import('./components/Header/YovoTripHeader')),
          { alwaysBrandBar: true }
        )}
      </React.Suspense>
      <ServiceRequestManagement />
    </Box>
  );
}

// Campaign Engine admin page wrapper
function CampaignsPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();

  React.useEffect(() => {
    if (!authLoading && (!isAuthenticated || !user?.isAdmin)) {
      navigate('/', { replace: true });
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#F5F6FA' }}>
        <CircularProgress sx={{ color: '#181B28' }} />
      </Box>
    );
  }

  if (!isAuthenticated || !user?.isAdmin) {
    return null;
  }

  return (
    <Box sx={{ bgcolor: '#F5F6FA', minHeight: '100vh' }}>
      <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>}>
        {React.createElement(
          React.lazy(() => import('./components/Header/YovoTripHeader')),
          { alwaysBrandBar: true }
        )}
      </React.Suspense>
      <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, md: 3 }, pt: { xs: 2, md: 3 }, pb: 4 }}>
        <Campaigns />
      </Box>
    </Box>
  );
}

function CampaignOffersPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();

  React.useEffect(() => {
    if (!authLoading && (!isAuthenticated || !user?.isAdmin)) {
      navigate('/', { replace: true });
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#F5F6FA' }}>
        <CircularProgress sx={{ color: '#181B28' }} />
      </Box>
    );
  }

  if (!isAuthenticated || !user?.isAdmin) {
    return null;
  }

  return (
    <Box sx={{ bgcolor: '#F5F6FA', minHeight: '100vh' }}>
      <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>}>
        {React.createElement(
          React.lazy(() => import('./components/Header/YovoTripHeader')),
          { alwaysBrandBar: true }
        )}
      </React.Suspense>
      <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, md: 3 }, pt: { xs: 2, md: 3 }, pb: 4 }}>
        <CampaignOffers />
      </Box>
    </Box>
  );
}

function BroadcastNotificationsPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();

  React.useEffect(() => {
    if (!authLoading && (!isAuthenticated || !user?.isAdmin)) {
      navigate('/', { replace: true });
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#F5F6FA' }}>
        <CircularProgress sx={{ color: '#181B28' }} />
      </Box>
    );
  }

  if (!isAuthenticated || !user?.isAdmin) {
    return null;
  }

  return (
    <Box sx={{ bgcolor: '#F5F6FA', minHeight: '100vh' }}>
      <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>}>
        {React.createElement(
          React.lazy(() => import('./components/Header/YovoTripHeader')),
          { alwaysBrandBar: true }
        )}
      </React.Suspense>
      <Box sx={{ maxWidth: 1080, mx: 'auto', px: { xs: 2, md: 3 }, pt: { xs: 2, md: 3 }, pb: 4 }}>
        <BroadcastNotifications />
      </Box>
    </Box>
  );
}

// Navigation wrapper component to use hooks inside Router
function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  /** Match MobileBottomNav: viewport width under MUI `sm` (600px) */
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  /** Same breakpoint as Homepage chatbot sidebar — `md` and up shows global chatbot UI */
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });
  /**
   * Revert: set `true` to show the bottom-right “Hey Its Yovo…” launcher + floating Chatbot on small screens again.
   */
  const SHOW_APP_CHATBOT_ON_MOBILE = false;
  const allowAppChatbot = SHOW_APP_CHATBOT_ON_MOBILE || isMdUp;
  const { isAuthenticated, loading } = useAuth();
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  const [flightData, setFlightData] = useState([]);
  const [farePlansModalOpen, setFarePlansModalOpen] = useState(false);
  const HOMEPAGE_MOBILE_NAV_SELECTOR = MOBILE_NAV_PORTAL_SELECTOR;

  // Remove SSR prerender guard so content becomes visible after React takes over
  useEffect(() => {
    const guard = document.getElementById('ssr-guard');
    if (guard) guard.remove();
  }, []);

  // Static shell covers the homepage until the real header and search card have painted.
  // Every other route drops it before paint.
  useLayoutEffect(() => {
    if (!isLcpShellPath(location.pathname)) removeLcpShell(document);
  }, [location.pathname]);

  useEffect(() => {
    if (!isLcpShellPath(location.pathname)) return undefined;
    let painted = 0;
    const first = window.requestAnimationFrame(() => {
      painted = window.requestAnimationFrame(() => removeLcpShell(document));
    });
    return () => {
      window.cancelAnimationFrame(first);
      window.cancelAnimationFrame(painted);
    };
  }, [location.pathname]);

  useEffect(() => {
    const warm = () => {
      warmVerticalLoaderImages();
      RouteSuspenseFallback.preloadLoaders();
    };
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(warm, { timeout: 4000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(warm, 1);
    return () => window.clearTimeout(id);
  }, []);

  // Disable browser scroll restoration - always start at top on navigation
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Scroll to top on every route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    trackMetaPageView(location.pathname, location.search);
    trackGtmPageView(location.pathname, location.search);
    syncClarityRoute(location.pathname);
  }, [location.pathname, location.search]);

  // Defensive cleanup: remove portaled mobile nav nodes when leaving primary mobile nav routes.
  useEffect(() => {
    const normalizedPath = normalizePathname(location.pathname);
    if (isPrimaryMobileNavRoute(normalizedPath) || typeof document === 'undefined') return;
    document.querySelectorAll(HOMEPAGE_MOBILE_NAV_SELECTOR).forEach((node) => {
      node.remove();
    });
  }, [location.pathname]);

  // Determine active navigation item based on current route
  const getActiveNavItem = () => {
    if (location.pathname.includes('/flight')) return 'flight';
    if (location.pathname.includes('/train')) return 'trains';
    if (location.pathname.includes('/hotel') || location.pathname.includes('/stays')) return 'stays';
    return 'flight'; // default
  };

  // Handle navigation clicks
  const handleNavigationClick = (itemId) => {
    switch (itemId) {
      case 'flight':
        navigate('/flight');
        break;
      case 'trains':
        // Add train route when available
        break;
      case 'stays':
        // Add hotel route when available
        break;
      default:
        break;
    }
  };

  // Expose the chatbot open function globally (no-op on mobile when allowAppChatbot is false)
  useEffect(() => {
    window.openChatbot = (initialMessage = '') => {
      if (!allowAppChatbot) return;
      setIsChatbotOpen(true);
      // If there's an initial message, we can store it for the chatbot to use
      if (initialMessage) {
        window.chatbotInitialMessage = initialMessage;
      }
    };
    // Expose function to set flight data
    window.setFlightDataForChatbot = setFlightData;
    // Expose function to open fare plans modal
    window.openFarePlans = () => {
      setFarePlansModalOpen(true);
    };
    return () => {
      delete window.openChatbot;
      delete window.setFlightDataForChatbot;
      delete window.chatbotInitialMessage;
      delete window.openFarePlans;
    };
  }, [allowAppChatbot]);

  useEffect(() => {
    if (!allowAppChatbot && isChatbotOpen) {
      setIsChatbotOpen(false);
    }
  }, [allowAppChatbot, isChatbotOpen]);

  const shouldShowChatbot = isChatbotOpen && allowAppChatbot;
  const pathNorm = normalizePathname(location.pathname);
  const sharePathActive = isAppSharePath(location.pathname);
  const routedPathname = sharePathActive ? toWebsitePathname(location.pathname) : location.pathname;
  const isProfileAccountRoute = pathNorm === '/account';
  const isHomepageRouteActive = isHomepageRoute(pathNorm);
  const showFloatingYovoBar =
    allowAppChatbot &&
    !isChatbotOpen &&
    !isHomepageRouteActive &&
    !sharePathActive &&
    !(isProfileAccountRoute && isMobileViewport);
  
  return (
    <>
      {needsStandaloneNoIndex(location.pathname) && (
        <PageHelmet
          title={isAdminPath(location.pathname) ? 'Admin' : 'Booking Confirmation'}
          description="Private YovoTrip page."
          noIndex
        />
      )}
      {/* Flight Details and Fare Plans Modal */}
      <Modal
        open={farePlansModalOpen}
        onClose={() => setFarePlansModalOpen(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 0, md: 2 },
          '@media (max-width: 699.98px)': {
            alignItems: 'flex-end',
            p: 0,
          },
          width: '100%',
          height: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box className="fdfp-fare-modal-shell">
          <FlightDetailsAndFarePlans 
            onClose={() => setFarePlansModalOpen(false)}
            tripType="roundtrip"
          />
        </Box>
      </Modal>

      {/* Main Content */}
      <ErrorBoundary>
        <Suspense fallback={<RouteSuspenseFallback pathname={routedPathname} />}>
          <Routes>
            {/* One Homepage instance for the tab URLs, so a tab tap does not remount and flash the previous tab. */}
            <Route element={<Homepage />}>
              <Route index />
              <Route path="flights" />
              <Route path="hotels" />
              <Route path="trains" />
              <Route path="deeplinks" />
              <Route path="deeplinks/hotels" />
              <Route path="deeplinks/flights" />
              <Route path="deeplinks/*" />
            </Route>
            <Route path="/flights/:routeSlug" element={<FlightSeoSelect />} />
            <Route path="/hotels/:citySlug" element={<HotelDestinationLandingPage />} />
            <Route path="/deeplinks/flights/:routeSlug" element={<FlightSeoSelect />} />
            <Route path="/deeplinks/hotels/:citySlug" element={<HotelDestinationLandingPage />} />
            <Route path="/deeplinks/flight" element={<DeeplinkFlightSelect />} />
            <Route path="/go/*" element={<GoToDeeplinksRedirect />} />
            <Route path="/go" element={<GoToDeeplinksRedirect />} />
            <Route path="/hotel" element={<HotelSearchPage />} />
            <Route path="/hotel/:hotelId" element={<RequireSearchParamRoute param={HOTEL_SESSION_QUERY_PARAM}><HotelDetailPage /></RequireSearchParamRoute>} />
            <Route path="/hotel/:hotelId/book/:roomId" element={<RequireSearchParamRoute param={HOTEL_SESSION_QUERY_PARAM}><BookingPage /></RequireSearchParamRoute>} />
            <Route path="/hotel/:hotelId/book/:roomId/review" element={<RequireSearchParamRoute param={HOTEL_SESSION_QUERY_PARAM}><BookingReviewPage /></RequireSearchParamRoute>} />
            <Route path="/hotel-booking-confirmation" element={<HotelBookingConfirmationPage />} />
            <Route path="/hotel-booking-confirmation/:bookingId" element={<HotelBookingConfirmationPage />} />
            <Route path="/hotel-cancel-review" element={<HotelCancellationReviewPage />} />
            <Route path="/train-results" element={<TrainResultsPage />} />
            <Route path="/train-results-page" element={<TrainResultsPage />} />
            <Route path="/yovo-trip-header" element={<YovoHeader />} />
            <Route path="/flight-search-header" element={<FlightSearchHeader />} />
            <Route path="/flight-fare-card" element={<FlightFareCard />} />
            <Route path="/flight_book" element={<FlightCard />} />
            <Route path="/flight-card" element={<FlightCardComponent />} />
            {/* <Route path="/yovo-trip-homepage" element={<Homepage2 />} /> */}
            <Route path="/flight" element={<RequireFlightSearchRoute><FlightSelect /></RequireFlightSearchRoute>} />
            <Route path="/train" element={<TrainSelect />} />
            <Route path="/train-details" element={<TrainDetailsPage />} />
            <Route path="/train-schedule" element={<TrainSchedulePage />} />
            <Route path="/pnr-status" element={<PNRStatusPage />} />
            <Route path="/flight-search" element={<FlightSearchPage />} />
            <Route path="/flight-booking" element={<FlightBookingPage />} />
            {/* <Route path="/flight-results" element={<FlightResultsDemo />} /> */}
            <Route path="/flight-search-results" element={<FlightSearchResults />} />
            <Route path="/flight-search-results-demo" element={<FlightSearchResultsDemo />} />
            <Route path="/flight-details" element={<FlightDetails />} />
            <Route path="/flight-booking-page" element={<RequireSearchParamRoute param={FLIGHT_SESSION_QUERY_PARAM}><FlightBookingPageNew /></RequireSearchParamRoute>} />
            <Route path="/seat-map-demo" element={<SeatMapDemo />} />
            <Route path="/seat-selection-demo" element={<SeatSelectionDemo />} />
            <Route path="/traveller-details" element={<TravellerDetails />} />
            <Route path="/flight-ssr-choose" element={<RequireSearchParamRoute param={FLIGHT_SESSION_QUERY_PARAM}><Flight_SSR_Choose /></RequireSearchParamRoute>} />
            <Route path="/flight-preview" element={<RequireSearchParamRoute param={FLIGHT_SESSION_QUERY_PARAM}><Flight_Preview /></RequireSearchParamRoute>} />
            <Route path="/payment" element={<RequireSearchParamRoute param={FLIGHT_SESSION_QUERY_PARAM}><PaymentPage /></RequireSearchParamRoute>} />
            <Route path="/b/:code" element={<ShortLinkRedirect />} />
            <Route path="/booking-confirmation" element={<BookingConfirmation />} />
            <Route path="/view-booking-details" element={<ViewBookingDetails />} />
            <Route path="/ticket/:token" element={<TicketView />} />
            <Route path="/flight-cancellation" element={<FlightCancellation />} />
            <Route path="/cancellation-review" element={<CancellationReview />} />
            <Route path="/cancelled-booking-details" element={<CancelledBookingDetails />} />
            <Route path="/e-ticket" element={<ETicket />} />
            <Route path="/seat-selection" element={
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#F5F6FA', p: 3 }}>
                <SeatSelectionPage />
              </Box>
            } />
            <Route path="/cancellation-policy" element={
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, bgcolor: '#F5F6FA', minHeight: '100vh' }}>
                <CancellationPolicy />
              </Box>
            } />
            <Route path="/test-animation" element={<TestAnimation />} />
            <Route path="/festivals/:festivalSlug" element={<FestivalGuideRoute />} />
            <Route path="/destinations" element={<DestinationsPage />} />
            <Route path="/destinations/:destinationSlug" element={<IstanbulGuide />} />
            <Route path="/destinations/heritage/:placeSlug" element={<HeritagePage />} />
            {/* Legacy URL redirects for SEO — keep old links working */}
            <Route path="/yovo_trip_guide" element={<Navigate to="/destinations/destination" replace />} />
            <Route path="/heritage-place/:placeName" element={<HeritageLegacyRedirect />} />
            <Route path="/my-trips" element={<MyTrips />} />
            <Route path="/account" element={<AccountPage />} />
            
            {/* Legal/Policy Pages */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/user-agreement" element={<TermsOfService />} />
            <Route path="/terms-of-service" element={<Navigate to={POLICY_ROUTES.userAgreement} replace />} />
            <Route path="/cancellation-refund-policy" element={<CancellationRefundPolicy />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/faqs" element={<FAQPage />} />
            <Route path="/sitemap" element={<SitemapPage />} />
            
            {/* Hotels Admin - Nested Routes */}
            <Route path="/admin/hotels" element={<AdminLayout section="hotels" />}>
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<HotelFinancials embedded />} />
              <Route path="bookings" element={<HotelBookingsConsole embedded />} />
              <Route path="pricing" element={<HotelPricingGovernance embedded />} />
              {/* Phase UX Cleanup: Simulator and Compare now integrated into Pricing page */}
              <Route path="pricing/simulator" element={<Navigate to="/admin/hotels/pricing" replace />} />
              <Route path="pricing/compare" element={<Navigate to="/admin/hotels/pricing" replace />} />
              <Route path="recon" element={<HotelRecon embedded />} />
              {/* Phase 1: Hotel Offers */}
              <Route path="offers" element={<HotelOffers embedded />} />
            </Route>
            
            {/* Flights Admin - Nested Routes */}
            <Route path="/admin/flights" element={<AdminLayout section="flights" />}>
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<FlightFinancials embedded />} />
              <Route path="bookings" element={<FlightBookingsConsole embedded />} />
              <Route path="session-json" element={<FlightSessionExport embedded />} />
              <Route path="insurance" element={<InsurancePolicyReport embedded />} />
              <Route path="cfar" element={<CfarReport embedded />} />
              <Route path="pricing" element={<FlightPricingGovernance embedded />} />
              <Route path="service-fees" element={<ServiceFeeManagement />} />
              <Route path="recon" element={<FlightRecon embedded />} />
              <Route path="offers" element={<FlightOffers embedded />} />
            </Route>
            
            {/* Issue Monitor - Cross-cutting diagnostic page */}
            <Route path="/admin/issues" element={
              <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>}>
                <IssueMonitorPage />
              </React.Suspense>
            } />
            
            {/* Refund Management - P0-2 Admin Refund Fallback */}
            <Route path="/admin/refunds" element={
              <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>}>
                <RefundManagementPage />
              </React.Suspense>
            } />
            <Route path="/admin/service-requests" element={
              <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>}>
                <ServiceRequestManagementPage />
              </React.Suspense>
            } />
            
            {/* Campaign Engine Admin */}
            <Route path="/admin/campaigns" element={
              <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>}>
                <CampaignsPage />
              </React.Suspense>
            } />
            <Route path="/admin/campaign-offers" element={
              <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>}>
                <CampaignOffersPage />
              </React.Suspense>
            } />
            <Route path="/admin/notifications" element={
              <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>}>
                <BroadcastNotificationsPage />
              </React.Suspense>
            } />
            
            {/* Legacy Admin Route Redirects (backward compatibility) */}
            <Route path="/admin/financials" element={<Navigate to="/admin/flights/overview" replace />} />
            <Route path="/admin/hotel-financials" element={<Navigate to="/admin/hotels/overview" replace />} />
            <Route path="/admin/hotel-pricing" element={<Navigate to="/admin/hotels/pricing" replace />} />
            <Route path="/admin/hotel-pricing-simulator" element={<Navigate to="/admin/hotels/pricing" replace />} />
            <Route path="/admin/hotel-bookings" element={<Navigate to="/admin/hotels/bookings" replace />} />
            <Route path="/admin/hotels/pricing/governance" element={<Navigate to="/admin/hotels/pricing" replace />} />
            
            <Route path="/fare-plans" element={
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '100vh',
                bgcolor: '#F5F6FA',
                p: 3
              }}>
                <FlightDetailsAndFarePlans 
                  onClose={() => navigate('/')}
                  tripType="roundtrip"
                />
              </Box>
            } />
            {/* <Route path="/airport-search-demo" element={<AirportSearchDemo />} /> */}
            {/* Global 404 — must remain the last route so all explicit routes match first */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
      {sharePathActive && (
        <Suspense fallback={null}>
          <AppStoreFallbackDrawer navigate={navigate} location={location} />
        </Suspense>
      )}

      {/* Chatbot - Compact Floating Window (Figma: ~420×660, bottom-right; avoid covering Let's Go) */}
      { shouldShowChatbot && (
        <Suspense fallback={null}>
        <Box
          sx={{
            position: 'fixed',
            bottom: { xs: '12px', md: '16px' },
            right: { xs: '12px', md: '20px' },
            width: {
              xs: 'min(420px, calc(100vw - 24px))',
              sm: 'min(420px, calc(100vw - 32px))',
              md: '420px',
            },
            height: {
              xs: 'min(660px, calc(100vh - 88px))',
              md: 'min(660px, calc(100vh - 96px))',
            },
            maxWidth: 'calc(100vw - 24px)',
            maxHeight: 'calc(100vh - 88px)',
            background: 'linear-gradient(135deg, #EAF5FC 0%, #F7F8FB 48%, #F6EAF2 100%)',
            zIndex: 1200,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '12px',
            boxShadow: '0 8px 28px rgba(0, 0, 0, 0.14)',
            border: 'none',
            overflow: 'hidden',
            pointerEvents: 'auto',
          }}
        >
          <Chatbot open={shouldShowChatbot} setOpen={setIsChatbotOpen} flightData={flightData} />
        </Box>
        </Suspense>
      )}

      {/* Floating Yovo Assist pill — collapsed launcher only */}
      {showFloatingYovoBar && (
        <Box
          role="button"
          tabIndex={0}
          aria-label="Open Yovo Assist"
          onClick={() => window.openChatbot && window.openChatbot()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              window.openChatbot && window.openChatbot();
            }
          }}
          sx={{
            position: 'fixed',
            right: { xs: '12px', sm: '16px', md: '20px' },
            bottom: { xs: '12px', sm: '16px', md: '20px' },
            width: { xs: 200, sm: 220, md: 228 },
            height: { xs: 54, sm: 56 },
            px: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #080809 0%, #15151A 100%)',
            border: '1px solid rgba(230, 25, 90, 0.45)',
            boxShadow:
              '0 4px 25px rgba(0,0,0,0.30), 0 0 18px rgba(225, 20, 85, 0.20)',
            // Above page content + sticky chrome (tabs 1098, headers ≤1100); below the open chat
            // panel / drawers / mobile booking bars (1200) and modals (1300).
            zIndex: 1150,
            transition: 'transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease',
            animation: 'yovoAssistGlow 2.8s ease-in-out infinite',
            '@keyframes yovoAssistGlow': {
              '0%, 100%': {
                boxShadow:
                  '0 4px 25px rgba(0,0,0,0.30), 0 0 14px rgba(225, 20, 85, 0.16)',
                borderColor: 'rgba(230, 25, 90, 0.40)',
              },
              '50%': {
                boxShadow:
                  '0 4px 28px rgba(0,0,0,0.32), 0 0 26px rgba(225, 20, 85, 0.34)',
                borderColor: 'rgba(230, 25, 90, 0.62)',
              },
            },
            '@keyframes yovoAssistShimmer': {
              '0%': { backgroundPosition: '120% 0' },
              '100%': { backgroundPosition: '-120% 0' },
            },
            '@keyframes yovoAssistSparkle': {
              '0%, 100%': { transform: 'scale(1) rotate(0deg)', opacity: 0.75 },
              '50%': { transform: 'scale(1.18) rotate(12deg)', opacity: 1 },
            },
            '@keyframes yovoAssistLogoPulse': {
              '0%, 100%': { boxShadow: '0 0 8px rgba(207, 32, 72, 0.32)' },
              '50%': { boxShadow: '0 0 16px rgba(207, 32, 72, 0.55)' },
            },
            '&:hover': {
              transform: 'translateY(-2px)',
              borderColor: 'rgba(230, 25, 90, 0.7)',
              animation: 'none',
              boxShadow:
                '0 6px 28px rgba(0,0,0,0.35), 0 0 24px rgba(225, 20, 85, 0.36)',
            },
            '&:focus-visible': {
              outline: '2px solid rgba(207, 32, 72, 0.7)',
              outlineOffset: 2,
            },
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
              '& img, & .yovo-assist-title, & .yovo-assist-sparkle': {
                animation: 'none !important',
              },
            },
          }}
        >
          <Box
            component="img"
            src={yovoAssistIcon}
            alt=""
            sx={{
              width: 34,
              height: 34,
              borderRadius: '9px',
              objectFit: 'cover',
              flexShrink: 0,
              boxShadow: '0 0 10px rgba(207, 32, 72, 0.4)',
              animation: 'yovoAssistLogoPulse 2.8s ease-in-out infinite',
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0, py: 0.15 }}>
            <Typography
              className="yovo-assist-title"
              sx={{
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                fontWeight: 700,
                lineHeight: 1.15,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                backgroundImage:
                  'linear-gradient(90deg, #fff 0%, #fff 38%, #FF8FB3 50%, #fff 62%, #fff 100%)',
                backgroundSize: '220% 100%',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                WebkitTextFillColor: 'transparent',
                animation: 'yovoAssistShimmer 3.4s ease-in-out infinite',
              }}
            >
              Yovo Assist
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.62)',
                fontSize: { xs: '0.5625rem', sm: '0.625rem' },
                fontWeight: 400,
                lineHeight: 1.2,
                mt: 0.15,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Your AI travel companion
            </Typography>
          </Box>
          <AutoAwesomeIcon
            className="yovo-assist-sparkle"
            sx={{
              fontSize: { xs: 13, sm: 14 },
              color: '#E91E63',
              flexShrink: 0,
              mr: 0.25,
              opacity: 0.95,
              animation: 'yovoAssistSparkle 2.2s ease-in-out infinite',
            }}
            aria-hidden
          />
        </Box>
      )}


    </>
  );
}

function App() {
  return (
    <HelmetProvider>
    <ThemeProvider>
      <AuthProvider>
      <AuthModalProvider>
      <SearchProvider>
        <Router>
          <AppContent />
        </Router>
      </SearchProvider>
      </AuthModalProvider>
      </AuthProvider>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
